<template>
  <div class="news-feed-page my-3">
    <div class="page-head card shadow-sm border-0 mb-3">
      <div class="card-body d-flex flex-wrap align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <span class="head-icon"><i class="bi bi-megaphone-fill"></i></span>
          <div>
            <h4 class="font-weight-bold mb-0">News &amp; Updates</h4>
            <p class="page-subtitle mb-0">
              All CCMIS announcements and release notes from the regional office.
            </p>
          </div>
        </div>
        <router-link
          v-if="isAdministrator"
          to="/news-updates/manage"
          class="btn btn-outline-primary btn-sm mt-2 mt-md-0"
        >
          <i class="bi bi-sliders mr-1"></i> Manage postings
        </router-link>
      </div>
    </div>

    <div class="card shadow-sm border-0">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5 text-muted">
          <span class="spinner-border spinner-border-sm mr-2"></span>
          Loading updates…
        </div>

        <div v-else-if="error" class="text-center py-5 text-muted">
          <i class="bi bi-exclamation-circle d-block mb-2"></i>
          {{ error }}
        </div>

        <div v-else-if="!updates.length" class="text-center py-5 text-muted">
          <i class="bi bi-newspaper d-block mb-2" style="font-size: 1.75rem; opacity: 0.5"></i>
          <p class="mb-0">No updates published yet.</p>
        </div>

        <div v-else class="feed-grid">
          <button
            v-for="item in updates"
            :key="item.id"
            type="button"
            class="feed-card"
            @click="openView(item)"
          >
            <span class="feed-badge" :class="'cat-' + item.category">
              <i class="bi mr-1" :class="categoryIcon(item.category)"></i>
              {{ categoryLabel(item.category) }}
            </span>
            <h5 class="feed-title">{{ item.title }}</h5>
            <p v-if="item.summary" class="feed-summary">{{ item.summary }}</p>
            <span class="feed-meta">
              {{ item.author_name }}
              · {{ formatUpdateDate(item.published_at || item.created_at) }}
            </span>
            <span class="feed-link">Read full update <i class="bi bi-arrow-right"></i></span>
          </button>
        </div>

        <div
          v-if="meta.total > meta.per_page"
          class="d-flex align-items-center justify-content-between pt-3 mt-2 border-top"
        >
          <small class="text-muted">
            Showing {{ rangeFrom }}–{{ rangeTo }} of {{ meta.total }}
          </small>
          <div class="btn-group btn-group-sm">
            <button
              type="button"
              class="btn btn-light"
              :disabled="meta.current_page <= 1"
              @click="goToPage(meta.current_page - 1)"
            >
              Previous
            </button>
            <button
              type="button"
              class="btn btn-light"
              :disabled="meta.current_page >= meta.last_page"
              @click="goToPage(meta.current_page + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" id="news-feed-view-modal" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content feed-view-modal">
          <div class="modal-header border-0 pb-0">
            <div class="flex-grow-1 pr-2">
              <span
                v-if="selectedUpdate"
                class="feed-badge"
                :class="'cat-' + selectedUpdate.category"
              >
                {{ categoryLabel(selectedUpdate.category) }}
              </span>
              <h5 class="modal-title font-weight-bold mt-2 mb-1">
                {{ selectedUpdate ? selectedUpdate.title : "" }}
              </h5>
              <small v-if="selectedUpdate" class="text-muted">
                {{ selectedUpdate.author_name }}
                · {{ formatUpdateDate(selectedUpdate.published_at || selectedUpdate.created_at, true) }}
              </small>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body pt-3">
            <p
              v-if="
                selectedUpdate &&
                selectedUpdate.summary &&
                !isForInformationCategory(selectedUpdate.category)
              "
              class="view-summary"
            >
              {{ selectedUpdate.summary }}
            </p>
            <div v-if="selectedUpdate" class="view-body">{{ selectedUpdate.body }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { enableModalBlur, disableModalBlur } from "../../js/modal-blur";
import {
  categoryIcon,
  categoryLabel,
  formatUpdateDate,
  isForInformationCategory,
} from "../../js/news-updates";

export default {
  name: "NewsUpdatesFeed",
  data() {
    return {
      updates: [],
      meta: {
        current_page: 1,
        per_page: 12,
        total: 0,
        last_page: 1,
      },
      loading: false,
      error: "",
      selectedUpdate: null,
    };
  },
  computed: {
    isAdministrator() {
      return this.$store.state.user && this.$store.state.user.role === "Administrator";
    },
    rangeFrom() {
      if (!this.meta.total) return 0;
      return (this.meta.current_page - 1) * this.meta.per_page + 1;
    },
    rangeTo() {
      return Math.min(this.meta.current_page * this.meta.per_page, this.meta.total);
    },
  },
  methods: {
    categoryIcon,
    categoryLabel,
    formatUpdateDate,
    isForInformationCategory,
    fetchUpdates(page = 1) {
      this.loading = true;
      this.error = "";

      return axios
        .get("api/system-updates", { params: { per_page: 12, page } })
        .then((res) => {
          this.updates = (res.data && res.data.data) || [];
          this.meta = (res.data && res.data.meta) || this.meta;
        })
        .catch(() => {
          this.error = "Unable to load news and updates.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    goToPage(page) {
      if (page < 1 || page > this.meta.last_page) return;
      this.fetchUpdates(page);
    },
    openView(item) {
      this.selectedUpdate = item;
      $("#news-feed-view-modal").modal("show");
    },
    openFromRoute() {
      const updateId = this.$route.query.update;
      if (!updateId) return;

      const match = this.updates.find((item) => String(item.id) === String(updateId));
      if (match) {
        this.openView(match);
        return;
      }

      axios
        .get(`api/system-updates/${updateId}`)
        .then((res) => {
          if (res.data) this.openView(res.data);
        })
        .catch(() => {});
    },
  },
  watch: {
    "$route.query.update"() {
      this.openFromRoute();
    },
  },
  mounted() {
    this.fetchUpdates().then(() => {
      this.openFromRoute();
    });
    const $modal = $("#news-feed-view-modal");
    $modal.on("shown.bs.modal", enableModalBlur);
    $modal.on("hidden.bs.modal", disableModalBlur);
  },
  beforeDestroy() {
    const $modal = $("#news-feed-view-modal");
    $modal.off("shown.bs.modal", enableModalBlur);
    $modal.off("hidden.bs.modal", disableModalBlur);
    disableModalBlur();
  },
};
</script>

<style scoped>
.page-head,
.card {
  border-radius: 14px !important;
}

.head-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(124, 92, 255, 0.12);
  color: #7c5cff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  margin-right: 1rem;
  flex-shrink: 0;
}

.page-subtitle {
  color: #768192;
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.feed-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.feed-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  padding: 1.05rem 1.1rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 21, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(21, 38, 63, 0.04);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.feed-card:hover {
  border-color: rgba(124, 92, 255, 0.28);
  box-shadow: 0 8px 22px rgba(124, 92, 255, 0.1);
  transform: translateY(-2px);
}

.feed-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 0.6rem;
}

.cat-changelog {
  background: rgba(43, 182, 163, 0.14);
  color: #1f8f7f;
}

.cat-announcement {
  background: rgba(124, 92, 255, 0.14);
  color: #7c5cff;
}

.cat-maintenance {
  background: rgba(224, 144, 31, 0.14);
  color: #c97812;
}

.cat-update {
  background: rgba(77, 141, 255, 0.14);
  color: #3d7ae8;
}

.cat-for_information {
  background: rgba(99, 111, 131, 0.14);
  color: #636f83;
}

.feed-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #15263f;
  line-height: 1.4;
  margin-bottom: 0.4rem;
}

.feed-summary {
  font-size: 0.84rem;
  color: #5c6b86;
  line-height: 1.5;
  margin-bottom: 0.65rem;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.feed-meta {
  font-size: 0.76rem;
  color: #9aa5b5;
  margin-bottom: 0.55rem;
}

.feed-link {
  font-size: 0.78rem;
  font-weight: 600;
  color: #7c5cff;
  margin-top: auto;
}

.feed-view-modal {
  border-radius: 14px;
  overflow: hidden;
}

.view-summary {
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #f4f7fb;
  color: #5c6b86;
  font-size: 0.92rem;
  line-height: 1.55;
}

.view-body {
  color: #2b3a55;
  font-size: 0.95rem;
  line-height: 1.7;
  white-space: pre-wrap;
}

@media (max-width: 991px) {
  .feed-grid {
    grid-template-columns: 1fr;
  }
}
</style>
